/**
 * Display helpers every OEP view shares (plan 07b-surface.md preamble). All dates and times are
 * US Eastern — the prototype's only zone (markup.html:349 "4:12 p.m. ET").
 */
const ET = 'America/New_York';

const dayMonth = new Intl.DateTimeFormat('en-GB', { timeZone: ET, day: 'numeric', month: 'long' });
const clock = new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', hour12: true });
const isoDate = new Intl.DateTimeFormat('en-CA', { timeZone: ET, year: 'numeric', month: '2-digit', day: '2-digit' });

/** "28 October" — the prototype's date style (logic.js:264 `uploaded: '28 October'`), on the US Eastern calendar day. */
export function DayMonth(d: Date): string {
    return dayMonth.format(d);
}

/** "4:12 p.m. ET" (markup.html:349). formatToParts, so the day period is read, not string-replaced. */
export function ClockET(d: Date): string {
    const parts = clock.formatToParts(d);
    const get = (t: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase() === 'AM' ? 'a.m.' : 'p.m.'} ET`;
}

/** "8.4 MB" / "640 KB" (markup.html:349, logic.js:264 `file: 'PDF, 8.4 MB'`); '' when the size is unknown. */
export function Size(bytes: number | null): string {
    if (bytes === null) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The US Eastern calendar date of `d` as YYYY-MM-DD (the format of IOEPTimelineDate.On). */
export function EasternISODate(d: Date): string {
    return isoDate.format(d);
}

/**
 * Whole days from today (US Eastern calendar) until the ET calendar date `on` (YYYY-MM-DD), floored
 * at 0 — calendar days, so "6 days" on 22 January for a 28 January deadline at any hour
 * (logic.js commStats), and "12 days left" on 12 November for 24 November (logic.js:282).
 */
export function DaysUntil(on: string, now: Date): number {
    const utc = (iso: string): number => {
        const [y, m, d] = iso.split('-').map(Number);
        return Date.UTC(y, m - 1, d);
    };
    return Math.max(0, Math.round((utc(on) - utc(EasternISODate(now))) / 86_400_000));
}

/** Short criterion labels, logic.js:30-61 RUBRIC[].short, indexed by ReviewQuestion.Sequence - 1. */
export const CRITERION_SHORT: readonly string[] = ['Scientific reasoning', 'Methodology', 'Conclusions', 'Use of time & space', 'Figures & visuals'];

/** "A", "A and B", "A, B and C" (logic.js:637 "Atmospheric Sciences and Hydrology"). */
export function JoinAnd(names: readonly string[]): string {
    return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "PDF" / "PPTX" / "PNG" / "JPEG" for a stored content type (logic.js:264 `file: 'PDF, 8.4 MB'`). */
export function FileTypeLabel(contentType: string | null, name: string): string {
    const byType: Readonly<Record<string, string>> = {
        'application/pdf': 'PDF',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
        'image/png': 'PNG',
        'image/jpeg': 'JPEG',
    };
    const known = contentType !== null ? byType[contentType.toLowerCase()] : undefined;
    if (known) return known;
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toUpperCase() : '';
}
